// frontend/src/features/ogdb2/OGDB2App.jsx
import React, { useEffect, useMemo, useState } from "react";
import { OGDB2 } from "./ogdb2Api";

import CreateWellBar from "./components/CreateWellBar";
import WellSearchPanel from "./components/WellSearchPanel";
import WellResultsTable from "./components/WellResultsTable";

import WellDetailPage from "./pages/WellDetailPage";
import BAIDDetailPage from "./pages/BAIDDetailPage";
import BulkAnalysisPage from "./pages/BulkAnalysisPage";
import ImportWizardPage from "./pages/ImportWizardPage";
import HomeDashboard from "./pages/HomeDashboard";

export default function OGDB2App({ email, onClose, logout }) {
  // home | detail | baid_detail | bulk | import
  const [mode, setMode] = useState("home");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);

  const [selectedWell, setSelectedWell] = useState(null);

  // ✅ BAID selection (used by Bulk Analysis → BAID Detail)
  const [selectedBAIDUnitId, setSelectedBAIDUnitId] = useState(null);
  const [selectedBAIDUnit, setSelectedBAIDUnit] = useState(null);
  const [selectedBAIDTaxYear, setSelectedBAIDTaxYear] = useState(null); // string like "2025"

  // Keep as single source of truth for "last search"
  const [lastSearch, setLastSearch] = useState({
    q: "",
    operator: "",
    county: "",
    appraisal_firm: "",
    appraiser: "",
    consultant: "",
    tax_year: new Date().getFullYear(),
    limit: 200,
    offset: 0,
    sort: "updated_at_desc",
    filters: [],
    metricFilters: [],
  });

  const title = useMemo(() => {
    if (mode === "detail") return "OGDB2 — Well Detail";
    if (mode === "baid_detail") return "OGDB2 — BAID Detail";
    if (mode === "bulk") return "OGDB2 — Analysis";
    if (mode === "import") return "OGDB2 — Import Wizard";
    return "OGDB2 — Home";
  }, [mode]);

  useEffect(() => {
    OGDB2.health().catch(() => {});
  }, []);

  const goApps = () => {
    if (typeof onClose === "function") onClose();
  };

  const setModeSafe = (nextMode) => {
    setError("");
    setMessage("");
    setMode(nextMode);

    // Clear selections when leaving their detail modes
    if (nextMode !== "detail") setSelectedWell(null);
    if (nextMode !== "baid_detail") {
      setSelectedBAIDUnitId(null);
      setSelectedBAIDUnit(null);
      setSelectedBAIDTaxYear(null);
    }
  };

  async function runSearch(params) {
    setIsSearching(true);
    setError("");
    setMessage("");

    const next = {
      ...lastSearch,
      ...params,
      limit: params?.limit ?? lastSearch.limit ?? 200,
      offset: params?.offset ?? 0,
      sort: params?.sort ?? lastSearch.sort ?? "updated_at_desc",
      filters: Array.isArray(params?.filters) ? params.filters : [],
      metricFilters: Array.isArray(params?.metricFilters) ? params.metricFilters : lastSearch.metricFilters || [],
    };

    try {
      if (next.filters && next.filters.length > 0) {
        const payload = {
          q: next.q || "",
          limit: next.limit,
          offset: next.offset,
          sort: next.sort,
          filters: next.filters,
        };
        const res = await OGDB2.searchWellsPost(payload);
        setResults(Array.isArray(res.data) ? res.data : []);
      } else {
        const res = await OGDB2.searchWells({
          q: next.q || "",
          operator: next.operator || "",
          county: next.county || "",
          appraisal_firm: next.appraisal_firm || "",
          appraiser: next.appraiser || "",
          consultant: next.consultant || "",
          tax_year: next.tax_year,
          limit: next.limit,
        });
        setResults(Array.isArray(res.data) ? res.data : []);
      }

      setLastSearch(next);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Search failed");
    } finally {
      setIsSearching(false);
    }
  }

  async function handleCreateWell(payload) {
    setError("");
    setMessage("");
    try {
      const res = await OGDB2.createWell(payload);
      setMessage("Created.");

      await runSearch(lastSearch);

      setSelectedWell(res.data);
      setModeSafe("detail");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Create failed");
    }
  }

  async function openWell(wellRow) {
    setError("");
    try {
      const res = await OGDB2.getWell(wellRow.id);
      setSelectedWell(res.data);
      setModeSafe("detail");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to open well");
    }
  }

  // ✅ Bulk Analysis → BAID Detail
  // BulkAnalysisPage currently calls onOpenUnit(unit), not onOpenUnit(row).
  // Support both shapes: (row with .unit) OR (unit itself).
  async function openBAIDFromBulk(rowOrUnit, taxYearFromBulk) {
    const unit =
      rowOrUnit?.unit ? rowOrUnit.unit : rowOrUnit || null;

    const unitId = unit?.id || null;
    if (!unitId) return;

    setSelectedBAIDUnitId(unitId);
    setSelectedBAIDUnit(unit); // good instant header while API loads
    setSelectedBAIDTaxYear(
      taxYearFromBulk !== undefined && taxYearFromBulk !== null && String(taxYearFromBulk).trim() !== ""
        ? String(taxYearFromBulk).trim()
        : null
    );
    setModeSafe("baid_detail");
  }

  function goHome() {
    setModeSafe("home");
  }

  const leftActive = useMemo(() => {
    if (mode === "bulk") return "analysis";
    if (mode === "import") return "import";
    return "home"; // home + detail + baid_detail count as home
  }, [mode]);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left nav */}
      <aside className="w-64 shrink-0 bg-white border-r border-gray-200">
        <div className="h-screen sticky top-0 flex flex-col">
          <div className="px-4 py-4 border-b border-gray-100">
            <div className="text-sm font-semibold text-gray-900">OGDB2</div>
            <div className="text-xs text-gray-500">Oil &amp; Gas Database</div>
          </div>

          <nav className="p-3 space-y-1">
            <LeftNavButton
              active={leftActive === "home"}
              onClick={goHome}
              label="Home"
              sub="Search + open wells"
            />
            <LeftNavButton
              active={leftActive === "analysis"}
              onClick={() => setModeSafe("bulk")}
              label="Analysis"
              sub="Bulk analysis & flags"
            />
            <LeftNavButton
              active={leftActive === "import"}
              onClick={() => setModeSafe("import")}
              label="Import Wizard"
              sub="CSV template + upload"
            />
          </nav>

          <div className="mt-auto p-3 space-y-2 border-t border-gray-100">
            <button className="btn w-full" onClick={goApps} title="Back to Apps" type="button">
              ← Apps
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
          <div className="max-w-[1800px] mx-auto px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-lg font-semibold text-gray-900">{title}</div>
              <div className="text-xs text-gray-500">Assessor vs Actual vs BA (values)</div>
            </div>

            <div className="flex items-center gap-2">
              <button className="btn-danger" onClick={logout} type="button">
                Logout
              </button>
            </div>
          </div>
        </div>

        <div
		  className={[
			"mx-auto px-4 py-6 space-y-4",
			mode === "bulk" ? "max-w-none w-full" : "max-w-[1800px]",
		  ].join(" ")}
		>
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {String(error)}
            </div>
          )}
          {message && (
            <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-700">
              {String(message)}
            </div>
          )}

          {mode === "home" && (
		    <HomeDashboard
			  email={email}
			  onGoAnalysis={() => setModeSafe("bulk")}
			  onGoImport={() => setModeSafe("import")}
			  onOpenBAID={openBAIDFromBulk}
			  onOpenAnalysisView={(view) => {
			    try {
				  localStorage.setItem("ogdb2_bulk_last_view_v3", JSON.stringify(view?.state || null));
			    } catch {}

			    try {
			  	  if (view?.id) localStorage.setItem("ogdb2_bulk_autoselect_view_id_v1", String(view.id));
				  localStorage.setItem("ogdb2_bulk_autorun_once_v1", "1");
			    } catch {}

			    setModeSafe("bulk");
			  }}
			/>
		  )}

          {mode === "detail" && selectedWell && (
            <WellDetailPage email={email} well={selectedWell} onBack={goHome} />
          )}

          {mode === "baid_detail" && selectedBAIDUnitId && (
            <BAIDDetailPage
              email={email}
              unitId={selectedBAIDUnitId}
              initialUnit={selectedBAIDUnit}
              initialTaxYear={selectedBAIDTaxYear}
              onBack={() => setModeSafe("bulk")}
            />
          )}

          {mode === "bulk" && (
            <BulkAnalysisPage
              onBack={goHome}
              // ✅ the key change:
              onOpenUnit={openBAIDFromBulk}
            />
          )}

          {mode === "import" && (
            <ImportWizardPage onRefresh={() => runSearch(lastSearch)} onGoAnalysis={() => setModeSafe("bulk")} />
          )}
        </div>

        <style>{`
          .btn { border:1px solid #e5e7eb; background:#fff; padding:8px 10px; border-radius:12px; font-size:14px; }
          .btn:hover { border-color:#d1d5db; }
          .btn-primary { border:1px solid #93c5fd; background:#eff6ff; color:#1d4ed8; padding:8px 10px; border-radius:12px; font-size:14px; }
          .btn-primary:hover { border-color:#60a5fa; }
          .btn-danger { border:1px solid #fecaca; background:#fff1f2; color:#9f1239; padding:8px 10px; border-radius:12px; font-size:14px; }
          .btn-danger:hover { border-color:#fca5a5; }
        `}</style>
      </div>
    </div>
  );
}

function LeftNavButton({ active, onClick, label, sub }) {
  return (
    <button
      onClick={onClick}
      className={[
        "w-full text-left rounded-xl border px-3 py-2 transition",
        active ? "border-blue-200 bg-blue-50" : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50",
      ].join(" ")}
      type="button"
    >
      <div className="text-sm font-semibold text-gray-900">{label}</div>
      {sub ? <div className="text-xs text-gray-500">{sub}</div> : null}
    </button>
  );
}
